import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';

import {
    Box, Text, Button, Divider, Image, Grid, Flex, Link
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import FormValidator from 'utils/FormValidator';
import UrlUtils from 'utils/Url';
import ErrorConstants from 'utils/ErrorConstants';
import * as legalConstants from 'constants/legal';
import TextInput from 'components/Inputs/TextInput/TextInput';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import PasswordRevealInput from 'components/Inputs/PasswordRevealInput';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import HelpersUtils from 'utils/Helpers';
import Markdown from 'components/Markdown/Markdown';
import ErrorList from 'components/ErrorList';
import ErrorMsg from 'components/ErrorMsg';
import BiRegisterForm from 'components/BiRegisterForm/BiRegisterForm';
import ReCaptcha from 'components/ReCaptcha/ReCaptcha';
import Tooltip from 'components/Tooltip/Tooltip';
import InfoButton from 'components/InfoButton/InfoButton';
import ReCaptchaText from 'components/ReCaptchaText/ReCaptchaText';

import { space } from 'style/config';
import RegisterEN from 'components/Register/locales/Register_en_US';
import RegisterFR from 'components/Register/locales/Register_fr_CA';
const getText = localeUtils.getLocalisedDataFromResource(RegisterEN, RegisterFR);
const FIELD_LENGTHS = FormValidator.FIELD_LENGTHS;
const ERROR_KEYS = {
    REGISTRATION_FRAUD_ERROR: 'profile.account.registrationFraudError'
};
const SUCCESS_DISPLAY_TIME_MS = 2000;

const RegisterForm = ({ handleSignIn, handleEmailChange, email }) => {
    const dispatch = useDispatch();
    const redirectTimeoutRef = useRef(null);
    const emailInput = useRef(null);
    const firstNameInput = useRef(null);
    const lastNameInput = useRef(null);
    const passwordInput = useRef(null);
    const mobilePhoneInput = useRef(null);
    const biRegForm = useRef(null);
    const reCaptcha = useRef(null);
    const isCanada = localeUtils.isCanada();
    const [errorMessages, setErrorMessages] = useState([]);
    const [errorMessageAndSignInHere, setErrorMessageAndSignInHere] = useState('');

    const [externalAuthParams, setExternalAuthParams] = useState({
        responseType: '',
        clientId: '',
        redirectUri: '',
        scope: '',
        state: '',
        codeChallenge: '',
        codeChallengeMethod: '',
        nonce: '',
        prompt: '',
        display: '',
        maxAge: '',
        uiLocales: '',
        idTokenHint: '',
        loginHint: '',
        acrValues: ''
    });
    const [joinBICheckbox, setJoinBICheckbox] = useState(false);
    const [isSSIEnabled, setIsSSIEnabled] = useState(false);
    const [zipCode, setZipCode] = useState('');
    const [mobilePhone, setMobilePhone] = useState('');
    const [isMarketingEnabled, setIsMarketingEnabled] = useState(false);
    const isOAuth2Flow = !!(externalAuthParams.clientId && externalAuthParams.redirectUri);
    const isCaptchaEnabled = Sephora.configurationSettings?.captchaRegistrationPopupEnabled;
    const isSSIEnabledConfig = Sephora.configurationSettings?.isOptInSSIMWebEnabled;
    const isEmailVerificationEnabled = Sephora.configurationSettings?.isEmailVerificationEnabled;
    const biData = {};

    const handleZipCodeChange = e => setZipCode(e.target.value);
    const toggleMarketing = () => setIsMarketingEnabled(!isMarketingEnabled);
    const handleToggleSSI = () => setIsSSIEnabled(!isSSIEnabled);
    const handleJoinBIClick = checked => setJoinBICheckbox(checked);

    const formatPhoneNumber = e => {
        const rawValue = e.target.value.replace(HelpersUtils.specialCharacterRegex, '');
        setMobilePhone(FormValidator.getFormattedPhoneNumber(rawValue, e.inputType));
    };

    const validateFirstName = firstName => {
        if (FormValidator.isEmpty(firstName)) {
            return ErrorConstants.ERRORS.FIRST_NAME.message();
        }

        return null;
    };

    const validateLastName = lastName => {
        if (FormValidator.isEmpty(lastName)) {
            return ErrorConstants.ERRORS.LAST_NAME.message();
        }

        return null;
    };

    const validatePassword = password => {
        if (FormValidator.isEmpty(password) || !FormValidator.isValidLength(password, 6, 12) || FormValidator.hasEmptySpaces(password)) {
            return ErrorConstants.ERRORS.PASSWORD.message();
        }

        return null;
    };

    const validateMobilePhone = mobilePhoneNo => {
        if (isMarketingEnabled && FormValidator.isEmpty(mobilePhoneNo)) {
            return ErrorConstants.ERRORS.MARKETING_PHONE_NUMBER.message();
        }

        if (mobilePhoneNo.length && !FormValidator.isValidPhoneNumber(mobilePhoneNo)) {
            return ErrorConstants.ERRORS.PHONE_NUMBER_INVALID_REGISTRATION.message();
        }

        return null;
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        const params = {
            responseType: urlParams.get('response_type') || '',
            clientId: urlParams.get('client_id') || '',
            redirectUri: urlParams.get('redirect_uri') || '',
            scope: urlParams.get('scope') || '',
            state: urlParams.get('state') || '',
            codeChallenge: urlParams.get('code_challenge') || '',
            codeChallengeMethod: urlParams.get('code_challenge_method') || '',
            nonce: urlParams.get('nonce') || '',
            prompt: urlParams.get('prompt') || '',
            display: urlParams.get('display') || '',
            maxAge: urlParams.get('max_age') || '',
            uiLocales: urlParams.get('ui_locales') || '',
            idTokenHint: urlParams.get('id_token_hint') || '',
            loginHint: urlParams.get('login_hint') || '',
            acrValues: urlParams.get('acr_values') || ''
        };

        setExternalAuthParams(params);

        // Pre-populate email if login_hint is provided
        if (params.loginHint) {
            handleEmailChange(null, params.loginHint);
        }

        // Store external auth params in localStorage for use after login (OAuth2 flow only)
        if (params.clientId && params.redirectUri) {
            Storage.local.setItem(LOCAL_STORAGE.EXTERNAL_AUTH_PARAMS, params);
        }

        return () => {
            if (redirectTimeoutRef.current) {
                clearTimeout(redirectTimeoutRef.current);
                redirectTimeoutRef.current = null;
            }
        };
    }, []);

    const validateForm = () => {
        const fieldsForValidation = [
            emailInput.current,
            firstNameInput.current,
            lastNameInput.current,
            passwordInput.current,
            mobilePhoneInput.current
        ];
        const errors = FormValidator.getErrors(fieldsForValidation);
        let isBiFormError = false;

        if (biRegForm.current) {
            isBiFormError = biRegForm.current.validateForm(true);
        }

        return !errors.fields.length && !isBiFormError;
    };

    const handleRegistrationError = error => {
        dispatch(Actions.showInterstice(false));

        if (error?.errors?.[ERROR_KEYS.REGISTRATION_FRAUD_ERROR]) {
            setErrorMessageAndSignInHere(error.errors[ERROR_KEYS.REGISTRATION_FRAUD_ERROR].join(' '));
        } else if (error?.errorMessages) {
            setErrorMessages(error.errorMessages);
        } else {
            setErrorMessages([getText('genericErrorMessage')]);
        }
    };

    const successToast = sessionId => {
        dispatch(
            Actions.showInfoModal({
                title: '',
                message: (
                    <Flex
                        flexDirection='column'
                        alignItems='center'
                        justifyContent='center'
                        gap={space[5]}
                        paddingTop={space[5]}
                        paddingBottom={space[6]}
                    >
                        <Image
                            src='/img/ufe/success-tick.svg'
                            alt='Success Icon'
                            disableLazyLoad={true}
                        />
                        <Text
                            fontSize='base'
                            textAlign='center'
                        >
                            {getText('congratulation1')}
                            <br />
                            {getText('congratulation2')}
                        </Text>
                    </Flex>
                ),
                showFooter: false,
                showCloseButton: false,
                width: 270,
                isOpen: true
            })
        );
        redirectTimeoutRef.current = setTimeout(() => {
            dispatch(Actions.showInfoModal({ isOpen: false }));
            sessionId && UrlUtils.redirectTo(`/authorize?session_id=${sessionId}`);
        }, SUCCESS_DISPLAY_TIME_MS);
    };

    const handleExternalAuthRegister = async optionParams => {
        const updatedParams = {
            ...optionParams,
            oauth2ClientId: externalAuthParams.clientId,
            oauth2RedirectUri: externalAuthParams.redirectUri,
            oauth2ResponseType: externalAuthParams.responseType,
            oauth2Scope: externalAuthParams.scope,
            oauth2State: externalAuthParams.state,
            oauth2CodeChallenge: externalAuthParams.codeChallenge,
            oauth2CodeChallengeMethod: externalAuthParams.codeChallengeMethod,
            oauth2Nonce: externalAuthParams.nonce
        };
        const successCallback = async response => {
            dispatch(Actions.showInterstice(false));

            if (response?.sessionId) {
                Storage.local.setItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID, response?.sessionId);
                successToast(response?.sessionId);
            } else {
                setErrorMessages([getText('genericErrorMessage')]);
                Storage.local.removeItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID);
            }
        };
        const failureCallback = response => {
            handleRegistrationError(response);
        };

        return dispatch(
            UserActions.register(updatedParams, successCallback, failureCallback, false, null, false, { isIntegrationRegisterAttempt: isOAuth2Flow })
        );
    };

    const handleRegularRegister = async optionParams => {
        const successCallback = () => {
            dispatch(Actions.showInterstice(false));
            UrlUtils.redirectTo('/');
        };
        const failureCallback = response => {
            handleRegistrationError(response);
        };

        return dispatch(UserActions.register(optionParams, successCallback, failureCallback, false, null, false));
    };

    const register = async captchaToken => {
        let biFormData = null;

        if (biRegForm.current) {
            biFormData = biRegForm.current.getBIDate();
        }

        const optionParams = {
            userDetails: {
                email: emailInput.current.getValue(),
                login: emailInput.current.getValue(),
                firstName: firstNameInput.current.getValue(),
                lastName: lastNameInput.current.getValue(),
                password: passwordInput.current.getValue(),
                confirmPassword: passwordInput.current.getValue()
            },
            registrationFrom: 'RegisterNormal',
            requestOrigin: 'Registration',
            isJoinBi: joinBICheckbox,
            subscription: {
                subScribeToEmails: localeUtils.isUS()
            },
            isKeepSignedIn: isSSIEnabled
        };

        if (captchaToken) {
            optionParams.captchaToken = captchaToken;
            optionParams.captchaLocation = 'REGISTRATION_POPUP';
        }

        const phoneNumberSanitized = mobilePhone.replace(/\D+/g, '');

        if (Sephora.configurationSettings.enableChangePhoneParameterBP1) {
            optionParams.userDetails.primaryPhone = phoneNumberSanitized;
        } else {
            optionParams.userDetails.phoneNumber = phoneNumberSanitized;
        }

        if (biFormData && biFormData.birthDay) {
            optionParams.userDetails.biAccount = biFormData;
            optionParams.isJoinBi = true;
        }

        if (zipCode) {
            optionParams.subscription.zipCode = zipCode.replace(/\s+/g, '');
        }

        if (isMarketingEnabled) {
            optionParams.subscription.subScribeToSms = isMarketingEnabled;
        }

        dispatch(Actions.showInterstice(true));

        try {
            if (isOAuth2Flow) {
                await handleExternalAuthRegister(optionParams);
            } else {
                await handleRegularRegister(optionParams);
            }
        } catch (error) {
            handleRegistrationError(error);
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setErrorMessages([]);
        setErrorMessageAndSignInHere('');

        if (!validateForm()) {
            return;
        }

        // Execute captcha if enabled
        if (isCaptchaEnabled && reCaptcha.current) {
            reCaptcha.current.execute();
        } else {
            register(null);
        }
    };

    const onCaptchaTokenReady = token => {
        if (token) {
            register(token);
        } else {
            reCaptcha.current?.reset();
        }
    };

    return (
        <Box
            lineHeight='tight'
            is='form'
            onSubmit={handleSubmit}
            noValidate
        >
            <Text
                is='h2'
                fontWeight='bold'
                fontSize='xl'
                marginTop={2}
                children={getText('title')}
            />
            <Image
                alt='Beauty Insider'
                disableLazyLoad={true}
                display='block'
                src='/img/ufe/bi/logo-beauty-insider.svg'
                width={194}
                height={32}
                marginBottom={4}
                marginTop={5}
            />

            <Markdown
                marginBottom={4}
                content={getText('joinBiFreeShip')}
            />

            {errorMessages.length > 0 && (
                <Box css={styles.errorContainer}>
                    <ErrorList errorMessages={errorMessages} />
                </Box>
            )}

            {/* In-Store User Error Message */}
            {errorMessageAndSignInHere && (
                <ErrorMsg
                    is='div'
                    marginBottom={4}
                >
                    <Text
                        is='p'
                        marginBottom='.5em'
                    >
                        {errorMessageAndSignInHere}
                    </Text>
                    <Text is='p'>
                        {getText('notYouMessage')},{' '}
                        <Link
                            onClick={handleSignIn}
                            fontWeight='bold'
                        >
                            {getText('signInHere')}
                        </Link>
                        .
                    </Text>
                </ErrorMsg>
            )}
            <>
                <Grid
                    gridTemplateColumns='1fr 1fr'
                    columnGap={4}
                >
                    <TextInput
                        label={getText('firstNameLabel')}
                        autoComplete='given-name'
                        autoCorrect='off'
                        hideAsterisk={true}
                        name='firstName'
                        required={true}
                        maxLength={FIELD_LENGTHS.name}
                        ref={firstNameInput}
                        validate={validateFirstName}
                    />

                    <TextInput
                        label={getText('lastNameLabel')}
                        autoComplete='family-name'
                        autoCorrect='off'
                        hideAsterisk={true}
                        name='lastName'
                        required={true}
                        maxLength={FIELD_LENGTHS.name}
                        ref={lastNameInput}
                        validate={validateLastName}
                    />
                </Grid>
                <InputEmail
                    marginBottom={4}
                    required={true}
                    name='email'
                    login={email}
                    label={getText('emailAddresLabel')}
                    hideAsterisk={true}
                    ref={emailInput}
                    disabled={true}
                />

                <PasswordRevealInput
                    id='register-password-input'
                    name='password'
                    ref={passwordInput}
                    label={getText('passwordLabel', [FIELD_LENGTHS.passwordMin, FIELD_LENGTHS.passwordMax])}
                    hideAsterisk={true}
                    validate={validatePassword} // 6-12 chars, no spaces
                />

                <Grid
                    gridTemplateColumns='1fr 1fr'
                    columnGap={4}
                >
                    <TextInput
                        name='mobilePhone'
                        label={getText('phoneNumber')}
                        autoComplete='tel'
                        autoCorrect='off'
                        type='tel'
                        maxLength={FormValidator.FIELD_LENGTHS.formattedPhone}
                        required={false}
                        value={mobilePhone}
                        onKeyDown={FormValidator.inputAcceptOnlyNumbers}
                        onChange={formatPhoneNumber}
                        onPaste={FormValidator.pasteAcceptOnlyNumbers}
                        ref={mobilePhoneInput}
                        validate={validateMobilePhone}
                    />
                    <Text
                        is='p'
                        fontSize='sm'
                        lineHeight='tight'
                        paddingY={2}
                    >
                        {getText('phoneNumberBenefit')}
                    </Text>
                </Grid>

                <Flex alignItems='center'>
                    <Checkbox
                        marginRight={2}
                        paddingY={0}
                        name='isMarketingEnabled'
                        checked={isMarketingEnabled}
                        onClick={toggleMarketing}
                    >
                        <Text fontWeight='bold'>{getText('marketingCheckbox')}</Text>
                    </Checkbox>
                </Flex>

                <BiRegisterForm
                    isRegisterModal={true}
                    isJoinBIChecked={joinBICheckbox}
                    isJoinBIDisabled={false}
                    biData={biData}
                    callback={handleJoinBIClick}
                    ref={biRegForm}
                />

                <Divider
                    marginBottom={4}
                    marginTop={4}
                />

                <TextInput
                    label={getText('zipCodeLabel')}
                    autoComplete='postal-code'
                    autoCorrect='off'
                    name='zipCode'
                    onChange={handleZipCodeChange}
                    value={zipCode}
                    maxLength={FIELD_LENGTHS.zipCode}
                />
                <Divider
                    marginBottom={4}
                    marginTop={4}
                />
                {/* Terms and Conditions */}
                <Box
                    color='gray'
                    fontSize='sm'
                    lineHeight='tight'
                    marginTop={4}
                >
                    <Text
                        is='p'
                        marginBottom='1em'
                    >
                        <Text
                            is='span'
                            fontWeight='bold'
                        >
                            {getText('disclosure')}
                        </Text>
                        {getText('byEntering')}
                        <Text
                            is='span'
                            fontWeight='bold'
                        >
                            {getText('signMeUp')}
                        </Text>
                        {getText('clicking')}
                        <Link
                            color='blue'
                            underline={true}
                            fontWeight='bold'
                            href={legalConstants.TEXT_TERM_LINK}
                            children={getText('textTerm')}
                        />
                        <Text is='span'>{getText('message')}</Text>
                        <Link
                            color='blue'
                            underline={true}
                            fontWeight='bold'
                            href={legalConstants.PRIVACY_POLICY_LINK}
                            children={getText('privacy')}
                        />
                        {isCanada ? (
                            ''
                        ) : (
                            <>
                                {getText('and')}
                                <Link
                                    color='blue'
                                    underline={true}
                                    fontWeight='bold'
                                    href={legalConstants.USNoticeIncentiveLink}
                                    children={getText('noticeOf')}
                                />
                            </>
                        )}
                        .{getText('textStop')}
                        {isCanada ? getText('textStopAddress') : ''}
                    </Text>
                    <Text
                        is='p'
                        marginBottom='1em'
                    >
                        {getText('byClicking')}{' '}
                        <Link
                            color='blue'
                            underline={true}
                            href={legalConstants.PRIVACY_POLICY_LINK}
                            children={getText('privacyPolicy')}
                        />
                        {localeUtils.isFrench() ? ' ' : ''}
                        {getText('and')}
                        <Link
                            color='blue'
                            underline={true}
                            target='_blank'
                            href={legalConstants.USNoticeIncentiveLink}
                            children={getText('noticeFinancialIncentive')}
                            {...(isEmailVerificationEnabled && { display: 'inline' })}
                        />
                        , (2),
                        {getText('agreeTo')}
                        <Link
                            color='blue'
                            underline={true}
                            fontWeight='bold'
                            href={legalConstants.TERMS_OF_USE_LINK}
                            children={getText('termsOfUse')}
                        />
                        {localeUtils.isFrench() ? ',' + getText('and') : ', '}
                        <Link
                            color='blue'
                            underline={true}
                            fontWeight='bold'
                            href={legalConstants.BEAUTY_INSIDER_TERMS_LINK}
                            children={getText('biTerms')}
                        />
                        {isCanada ? '.' : getText('receiveOffers')}
                    </Text>
                </Box>
                {isSSIEnabledConfig && (
                    <Flex
                        marginY={4}
                        alignItems='center'
                    >
                        <Checkbox
                            marginRight={2}
                            paddingY={2}
                            name='isSSIEnabled'
                            id='register_ssi'
                            checked={isSSIEnabled}
                            onClick={handleToggleSSI}
                        >
                            {getText('staySignedInLabel')}
                        </Checkbox>
                        <Tooltip
                            content={getText('staySignedInTooltip')}
                            dismissButton={true}
                            fontSize='sm'
                        >
                            <InfoButton />
                        </Tooltip>
                    </Flex>
                )}
                <Button
                    variant='primary'
                    width='100%'
                    type='submit'
                >
                    {getText('joinNow')}
                </Button>
            </>

            <Divider marginY={4} />
            {isCaptchaEnabled && <ReCaptchaText isRegisterModal={true} />}
            {isCaptchaEnabled && (
                <ReCaptcha
                    ref={reCaptcha}
                    onChange={onCaptchaTokenReady}
                    siteKey={Sephora.configurationSettings.recaptchaSiteKey}
                />
            )}
        </Box>
    );
};

const styles = {
    errorContainer: {
        marginBottom: space[4]
    }
};

export default wrapFunctionalComponent(RegisterForm, 'RegisterForm');
